import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { CalendarDays, Plus, Trash2, Edit2, Repeat } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { calendarAnnotations as annotationsApi } from '../utils/api';

/**
 * CalendarAnnotationsManager — admin CRUD for calendar annotations
 * (roadmap item #18). Pin custom events / notes / reminders to
 * specific dates without gating booking. Mirrors ShopClosuresManager
 * shape so admins find it familiar.
 */

const COLOR_OPTIONS = [
  { value: 'blue',   label: 'Blue',   className: 'bg-blue-500' },
  { value: 'green',  label: 'Green',  className: 'bg-green-500' },
  { value: 'purple', label: 'Purple', className: 'bg-purple-500' },
  { value: 'orange', label: 'Orange', className: 'bg-orange-500' },
  { value: 'red',    label: 'Red',    className: 'bg-red-500' },
  { value: 'gray',   label: 'Gray',   className: 'bg-gray-500' },
];

const EMPTY_FORM = {
  annotationDate: '',
  title: '',
  description: '',
  color: 'blue',
  isRecurring: false,
};

const CalendarAnnotationsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // full row for edit
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await annotationsApi.list();
      setItems(res.data || []);
    } catch (e) {
      console.error('Load annotations error:', e);
      toast.error('Failed to load calendar annotations');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (row) => {
    setEditing(row);
    setForm({
      annotationDate: String(row.annotation_date).split('T')[0],
      title: row.title || '',
      description: row.description || '',
      color: row.color || 'blue',
      isRecurring: !!row.is_recurring,
    });
    setShowAdd(true);
  };

  const closeModal = () => {
    setShowAdd(false);
    setEditing(null);
    setForm({ ...EMPTY_FORM });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.annotationDate) return toast.error('Pick a date');
    if (!form.title.trim()) return toast.error('Title is required');
    setSubmitting(true);
    try {
      if (editing) {
        await annotationsApi.update(editing.id, form);
        toast.success('Annotation updated');
      } else {
        await annotationsApi.create(form);
        toast.success('Annotation added');
      }
      closeModal();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete annotation "${row.title}"?`)) return;
    try {
      await annotationsApi.delete(row.id);
      toast.success('Annotation removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <div className="card flex items-center justify-center py-6">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
      </div>
    );
  }

  const colorDot = (color) => {
    const opt = COLOR_OPTIONS.find((c) => c.value === color);
    return <span className={`inline-block w-3 h-3 rounded-full ${opt?.className || 'bg-gray-400'}`} />;
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center space-x-3">
          <CalendarDays className="text-primary-600" size={24} />
          <h2 className="text-2xl font-bold">Calendar Annotations</h2>
        </div>
        <button onClick={() => { setEditing(null); setForm({ ...EMPTY_FORM }); setShowAdd(true); }} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> Add annotation
        </button>
      </div>

      <p className="text-gray-600 mb-4 text-sm">
        Pin events, holidays, or reminders to specific dates. Annotations are
        informational only — they appear on the calendar but never block
        bookings. Use Shop Closures below for that.
      </p>

      {items.length === 0 ? (
        <p className="text-gray-500 text-sm">No annotations yet.</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {items.map((row) => (
            <li key={row.id} className="py-3 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                {colorDot(row.color)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900">{row.title}</span>
                    {row.is_recurring && (
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <Repeat size={10} /> Yearly
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {row.is_recurring
                      ? `Every ${format(parseISO(String(row.annotation_date).split('T')[0]), 'MMMM d')}`
                      : format(parseISO(String(row.annotation_date).split('T')[0]), 'EEE, MMM d, yyyy')}
                  </div>
                  {row.description && (
                    <div className="text-sm text-gray-700 mt-1 break-words">{row.description}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => startEdit(row)} className="p-2 text-primary-600 hover:bg-primary-50 rounded" title="Edit">
                  <Edit2 size={16} />
                </button>
                <button onClick={() => handleDelete(row)} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">{editing ? 'Edit annotation' : 'Add annotation'}</h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  className="input"
                  value={form.annotationDate}
                  onChange={(e) => setForm({ ...form, annotationDate: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  className="input"
                  maxLength={200}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Grand Opening, Staff Party"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  className="input"
                  rows={3}
                  value={form.description || ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {COLOR_OPTIONS.map((opt) => (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setForm({ ...form, color: opt.value })}
                      className={`w-8 h-8 rounded-full ${opt.className} ${form.color === opt.value ? 'ring-2 ring-offset-2 ring-primary-600' : ''}`}
                      title={opt.label}
                    />
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isRecurring}
                  onChange={(e) => setForm({ ...form, isRecurring: e.target.checked })}
                />
                <span className="text-sm">Repeat every year on this date</span>
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="btn btn-secondary" disabled={submitting}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving…' : editing ? 'Save changes' : 'Add annotation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarAnnotationsManager;
